import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";

interface AsyncResource<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  reload: () => void;
  setData: Dispatch<SetStateAction<T | null>>;
}

export function useAsyncResource<T>(loader: () => Promise<T>, dependencies: readonly unknown[] = []): AsyncResource<T> {
  const loaderRef = useRef(loader);
  loaderRef.current = loader;
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [revision, setRevision] = useState(0);

  const reload = useCallback(() => setRevision((value) => value + 1), []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    loaderRef.current()
      .then((result) => {
        if (active) setData(result);
      })
      .catch((caught: unknown) => {
        if (active) setError(caught instanceof Error ? caught : new Error("Unexpected request error."));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [revision, ...dependencies]);

  return { data, loading, error, reload, setData };
}
